import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { JhiEventManager, JhiLanguageService } from '../shared/core-utils';
import { GermplasmListService } from '../shared/germplasm-list/service/germplasm-list.service';
import { AlertService } from '../shared/alert/alert.service';
import { Principal } from '../shared';
import { TranslateService } from '@ngx-translate/core';
import { StudyEntryService } from '../shared/study/service/study-entry.service';
import { ParentModel } from '../shared/study/model/parent.model';
import { DesignCrossesContext } from './design-crosses.context';
import { CrossSourceEntryModel } from '../shared/study/model/cross-source-entry.model';
import { ColumnHeadersEnum } from '../shared/study/model/column-header.enum';
import { SORT_PREDICATE_NONE } from '../germplasm-manager/germplasm-search-resolve-paging-params';
import { GermplasmDetailsPopupService } from '../germplasm-details/germplasm-details-popup.service';
import { MANAGE_ACCEPT_PENDING_OBSERVATION_VALUES_PERMISSIONS, MANAGE_CONFIRMED_OBSERVATION_VALUES_PERMISSIONS } from '../shared/auth/permissions';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { MAX_NAME_DISPLAY_SIZE } from '../app.constants';

@Component({
    selector: 'jhi-source-list',
    templateUrl: './source-list.component.html',
})
export class SourceListComponent implements OnInit, OnChanges {

    @Input() listId: number;
    @Input() listName: string;
    @Input() studyId: number;

    MAX_NAME_DISPLAY_SIZE = MAX_NAME_DISPLAY_SIZE;
    user?: any;

    crossSourceEntries: CrossSourceEntryModel[] = [];
    originalList: CrossSourceEntryModel[] = [];

    selectedItems: Map<number, CrossSourceEntryModel> = new Map<number, CrossSourceEntryModel>();
    lastClickIndex: any;
    totalItems: number;

    isSelectAll: boolean;

    predicate: any;
    reverse: boolean;
    ColumnHeaders = ColumnHeadersEnum;
    private currentPopover: NgbPopover;

    constructor(private jhiLanguageService: JhiLanguageService,
                private eventManager: JhiEventManager,
                private germplasmListService: GermplasmListService,
                private studyEntryService: StudyEntryService,
                private alertService: AlertService,
                public principal: Principal,
                public translateService: TranslateService,
                private designCrossesContext: DesignCrossesContext,
                protected germplasmDetailsPopupService: GermplasmDetailsPopupService
    ) {
    }

    ngOnChanges(changes: SimpleChanges): void {
        // Reinitialize component if the input changes
        this.initialize();
    }

    async ngOnInit() {
    }

    private async initialize() {
        const identity = await this.principal.identity();
        this.user = identity;
        this.crossSourceEntries = [];
        this.originalList = [];
        this.selectedItems.clear();

        if (this.studyId) {
            const sort = ['8230,asc'];
            // Pass germplasm descriptor ids of DESIGNATION and GROUPGID to make sure both are retrieved
            const germplasmDescriptorIds  = [8250, 8330];
            this.studyEntryService.getAllStudyEntries(this.studyId, sort, germplasmDescriptorIds).toPromise().then((entries) => {
                entries.forEach((entry) => {
                    this.crossSourceEntries.push(new CrossSourceEntryModel(entry.entryId, entry.entryNumber, entry.gid,
                        entry.designation, entry.cross, entry.groupGid, this.designCrossesContext.studyName));
                });
                this.totalItems = entries.length;
                this.originalList = this.crossSourceEntries;
            });
        } else if (this.listId) {
            this.germplasmListService.postSearchListData(this.listId, {}).subscribe((searchRequestId: string) => {
                const sort = ['VARIABLE_8230,asc'];
                this.germplasmListService.getAllGermplasmListData(this.listId, searchRequestId, sort).toPromise().then((entries) => {
                    entries.forEach((entry) => {
                        this.crossSourceEntries.push(new CrossSourceEntryModel(entry.listDataId, entry.data['VARIABLE_8230'], entry.data['GID'],
                            entry.data['DESIGNATION'], entry.data['CROSS'], entry.data['GROUP_ID'], this.designCrossesContext.activeListName));
                    });
                    this.totalItems = entries.length;
                    this.originalList = this.crossSourceEntries;
                });
            });
        }
        this.predicate = SORT_PREDICATE_NONE;
        this.reverse = false;
    }

    trackId(item: CrossSourceEntryModel) {
        return item.entryId;
    }

    isSelected(entry: CrossSourceEntryModel) {
        return this.selectedItems.get(entry.entryId);
    }

    isPageSelected() {
        return this.crossSourceEntries.every((entry: CrossSourceEntryModel) => Boolean(this.selectedItems.get(entry.entryId)));
    }

    dragStart($event, dragged: CrossSourceEntryModel) {
        let selected;
        if (this.selectedItems.has(dragged.entryId)) {
            // TODO sort as in table
            selected = Array.from(this.selectedItems.values());
            selected.sort((a, b) => Number(a.entryNumber) > Number(b.entryNumber) ? 1 : -1);
        } else {
            selected = [dragged];
        }
        this.designCrossesContext.draggedItems = selected;
    }

    dragEnd($event) {
    }

    onSelectPage() {
        if (this.isPageSelected()) {
            // remove all items
            this.selectedItems.clear();
        } else {
            // check remaining items
            this.crossSourceEntries.forEach((entry: CrossSourceEntryModel) => this.selectedItems.set(entry.entryId, entry));
            this.crossSourceEntries.forEach((entry: CrossSourceEntryModel) => {
                this.selectedItems.set(entry.entryId, entry);
            });
        }
        this.isSelectAll = this.isPageSelected();
    }

    selectAll(isSelectAll: boolean) {
        this.isSelectAll = isSelectAll;
        if (isSelectAll) {
            this.clearSelectedItems();
            this.onSelectPage();
        } else {
            this.clearSelectedItems();
        }
    }

    selectEvenEntries() {
        this.clearSelectedItems();
        this.crossSourceEntries.forEach((entry: CrossSourceEntryModel) => {
            if (entry.entryNumber % 2 === 0) {
                this.selectedItems.set(entry.entryId, entry);
            }
        });
    }

    selectOddEntries() {
        this.clearSelectedItems();
        this.crossSourceEntries.forEach((entry: CrossSourceEntryModel) => {
            if (entry.entryNumber % 2 === 1) {
                this.selectedItems.set(entry.entryId, entry);
            }
        });
    }

    clearSelectedItems() {
        this.selectedItems.clear();
    }

    toggleSelect($event, index, entry: CrossSourceEntryModel, checkbox = false) {

        if (!$event.ctrlKey && !$event.metaKey && !checkbox) {
            this.clearSelectedItems();
        }

        let items;
        if ($event.shiftKey) {
            const max = Math.max(this.lastClickIndex, index) + 1,
                min = Math.min(this.lastClickIndex, index);
            items = this.crossSourceEntries.slice(min, max);
        } else {
            items = [entry];
            this.lastClickIndex = index;
        }
        const isClickedItemSelected = this.selectedItems.get(entry.entryId);
        for (const item of items) {
            if (isClickedItemSelected) {
                this.selectedItems.delete(item.entryId);
            } else {
                this.selectedItems.set(item.entryId, item);
            }
        }
        this.isSelectAll = this.isPageSelected();
    }

    addToParentsList(isAddToFemaleParents: boolean) {
        if (this.selectedItems.size === 0) {
            this.alertService.warning('design-crosses.selection.empty');
            return;
        }
        const parents: ParentModel[] = [];
        this.selectedItems.forEach((entry) => {
            parents.push(new ParentModel(entry.entryId, 0, entry.entryNumber, entry.gid, entry.designation,
                entry.cross, entry.sourceList, this.designCrossesContext.studyName));
        });
        parents.sort((a, b) => Number(a.entryNumber) > Number(b.entryNumber) ? 1 : -1);
        const content = {
            'isAddToFemaleParents': isAddToFemaleParents,
            'parents': parents
        };
        this.eventManager.broadcast({ name: 'addToParents', content });
    }

    sort() {
        this.crossSourceEntries = [...this.originalList];
        if (this.predicate === this.ColumnHeaders.ENTRY_NO) {
            this.crossSourceEntries.sort((a, b) => {
                if (this.reverse) {
                    return Number(a.entryNumber) > Number(b.entryNumber) ? 1 : -1;
                } else {
                    return Number(a.entryNumber) < Number(b.entryNumber) ? 1 : -1;
                }
            });
        } else if (this.predicate === this.ColumnHeaders.DESIGNATION) {
            this.crossSourceEntries.sort((a, b) => {
                if (this.reverse) {
                    return a.designation > b.designation ? 1 : -1;
                } else {
                    return a.designation < b.designation ? 1 : -1;
                }
            });
        } else if (this.predicate === this.ColumnHeaders.CROSS) {
            this.crossSourceEntries.sort((a, b) => {
                if (this.reverse) {
                    return a.cross > b.cross ? 1 : -1;
                } else {
                    return a.cross < b.cross ? 1 : -1;
                }
            });
        } else if (this.predicate === this.ColumnHeaders.GID) {
            this.crossSourceEntries.sort((a, b) => {
                if (this.reverse) {
                    return Number(a.gid) > Number(b.gid) ? 1 : -1;
                } else {
                    return Number(a.gid) < Number(b.gid) ? 1 : -1;
                }
            });
        } else if (this.predicate === this.ColumnHeaders.GROUP_ID) {
            this.crossSourceEntries.sort((a, b) => {
                if (this.reverse) {
                    return Number(a.groupGid) > Number(b.groupGid) ? 1 : -1;
                } else {
                    return Number(a.groupGid) < Number(b.groupGid) ? 1 : -1;
                }
            });
        }
    }

    showRightClickMenu($event, p: NgbPopover) {
        if (this.currentPopover) {
            this.currentPopover.close();
        }
        if (this.crossSourceEntries && this.crossSourceEntries.length > 0) {
            $event.preventDefault();
            this.currentPopover = p;
            p.open();
        }
    }

    protected readonly MANAGE_CONFIRMED_OBSERVATION_VALUES_PERMISSIONS = MANAGE_CONFIRMED_OBSERVATION_VALUES_PERMISSIONS;
    protected readonly MANAGE_ACCEPT_PENDING_OBSERVATION_VALUES_PERMISSIONS = MANAGE_ACCEPT_PENDING_OBSERVATION_VALUES_PERMISSIONS;
}
